import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import useConditionalWhyDidYouUpdate from '../../../../hooks/useConditionalWhyDidYouUpdate';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { getDomProps } from './getDomProps';
import { useTreeContext } from './TreeContext';
import TreeItemChildren from './TreeItemChildren';
import TreeItemLabel from './TreeItemLabel';
import { TDataNode, TId } from './types';

const TreeItem = ({ level, treeKey }: { level: number; treeKey: TId }) => {
  const { handler, name, forceUpdate, treeProps } = useTreeContext();

  const props = usePropsSelector<
    TDataNode<Record<string, unknown>>,
    TDataNode<Record<string, unknown>>
  >(treeKey, {
    selector: (current) => current,
    comparator: (prevProps, newProps) => {
      return (
        shallowEqual(prevProps, newProps) &&
        shallowEqual(prevProps?.children, newProps?.children)
      );
    },
    propsStore: handler.propsStore,
  });

  useConditionalWhyDidYouUpdate(
    String(treeKey),
    { ...props, treeKey, handler, name },
    false,
  );

  const nodes = React.useMemo(
    () =>
      props.children?.map((current) =>
        handler.propsStore.getFieldProps(current),
      ) ?? [],
    [props.children, handler.propsStore],
  );

  return (
    <>
      <Box
        className={props.className}
        as="li"
        {...getDomProps(treeProps, name, 'node', props)}
      >
        <TreeItemLabel level={level} treeKey={treeKey} />
        <TreeItemChildren
          forceUpdate={forceUpdate}
          role="group"
          level={level}
          nodes={nodes}
        />
      </Box>
    </>
  );
};

export default React.memo(TreeItem);
